import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import request from "../../../Helpers/API";
import PlantillaScreenRemastered from "../../CRM/PlantillaScreenRemastered";
import { LoaderScreen } from "../../components/LoaderScreen";
import PaginadorListado from "../../components/PaginadorListado";
import { FormatImporteNullable } from "../../../Helpers/FormatNumber";
import { isoADDMMYYYY } from "../../../Helpers/FormatFecha";
import { construirQuery } from "../../../Helpers/QueryString";
import { useDescargaExcel } from "../../../Helpers/hooks/useDescargaExcel";
import FiltroPanel, { contarFiltrosActivos } from "../../components/Filtro/FiltroPanel";

// Reporte de préstamos a clientes (base: /api/ReportePrestamosClientes).
// Se abre desde la card "Préstamos clientes" de DashboardCobranzaScreen2.
// Los filtros son opcionales: si no se usan, NO se mandan en la query string.
const TITULO_SWAL = "PRÉSTAMOS CLIENTES";
const RUTA_DASHBOARD = "/Creditoycobranza/DashboardCobranza";
const MENSAJE_ERROR_GENERICO =
  "No fue posible completar la operación. Intente nuevamente o contacte al administrador.";
const REGISTROS_POR_PAGINA = 50;
const SIN_VALOR = "—";

const ORIGEN_ESTILO = {
  "FACTURACION CON SOLICITUD": "ok",
  "FACTURACION SIN SOLICITUD": "alerta",
  "SOLICITUD SIN FACTURACION": "aviso",
};

const COLUMNAS = [
  { campo: "origen_registro", etiqueta: "Origen" },
  { campo: "documento", etiqueta: "Documento" },
  { campo: "serie", etiqueta: "Serie" },
  { campo: "folio", etiqueta: "Folio" },
  { campo: "razon_social", etiqueta: "Cliente" },
  { campo: "rfc", etiqueta: "RFC" },
  { campo: "vencimiento", etiqueta: "Vencimiento", tipo: "fecha" },
  { campo: "importe_factura", etiqueta: "Importe factura", tipo: "importe" },
  { campo: "importe_pagado", etiqueta: "Importe pagado", tipo: "importe" },
  { campo: "saldo", etiqueta: "Saldo", tipo: "importe" },
  { campo: "folio_solicitud", etiqueta: "Folio solicitud" },
];

const CAMPOS_BUSQUEDA = ["documento", "serie", "folio", "razon_social", "rfc", "folio_solicitud"];
const CAMPOS_IMPORTE = COLUMNAS.filter((c) => c.tipo === "importe").map((c) => c.campo);

const FILTROS_INICIALES = { origen_registro: "", fecha_desde: "", fecha_hasta: "" };

// Valida en front antes de llamar al backend. Devuelve mensaje de error o null.
const validarFiltros = ({ fecha_desde, fecha_hasta }) => {
  if (fecha_desde && fecha_hasta && fecha_desde > fecha_hasta) {
    return "La fecha desde no puede ser posterior a la fecha hasta.";
  }
  return null;
};

// El backend responde 400 con { mensaje }. Se muestra tal cual; si no viene, mensaje genérico.
const mostrarError = (error) => {
  const mensaje = typeof error?.mensaje === "string" && error.mensaje.trim() ? error.mensaje : MENSAJE_ERROR_GENERICO;
  Swal.fire(TITULO_SWAL, mensaje, "error");
};

const esNumero = (valor) => valor !== null && valor !== undefined && valor !== "" && Number.isFinite(Number(valor));

const formatearCelda = (valor, tipo) => {
  if (valor === null || valor === undefined || valor === "") return SIN_VALOR;
  if (tipo === "importe") return FormatImporteNullable(valor) || SIN_VALOR;
  if (tipo === "fecha") return isoADDMMYYYY(valor) || SIN_VALOR;
  return String(valor);
};

export const ReportePrestamoClientesMainScreen = () => {
  const history = useHistory();
  const [origenes, setOrigenes] = useState([]);
  // Filtros con los que se obtuvo el listado actual (el Excel usa estos, no los del formulario).
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_INICIALES);
  const [listado, setListado] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(REGISTROS_POR_PAGINA);
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });

  const mostrarLoader = (mostrar) => setLoader((prev) => ({ ...prev, mostrar }));

  const cargarOrigenes = () =>
    request
      .get("ReportePrestamosClientes/Origenes")
      .then((result) => setOrigenes(Array.isArray(result) ? result : []))
      .catch(() => setOrigenes([]));

  const cargarListado = (filtrosConsulta) => {
    mostrarLoader(true);
    request
      .get(`ReportePrestamosClientes/Listado${construirQuery(filtrosConsulta)}`)
      .then((result) => {
        setListado(Array.isArray(result) ? result : []);
        setFiltrosAplicados(filtrosConsulta);
        setPagina(1);
      })
      .catch(mostrarError)
      .finally(() => mostrarLoader(false));
  };

  const descargarExcel = useDescargaExcel(TITULO_SWAL, setLoader);
  const generarExcel = () =>
    descargarExcel("ReportePrestamosClientes/GenerarExcel", filtrosAplicados, "REPORTE DE PRESTAMOS CLIENTES");

  // Campos del filtro reutilizable (la validación de fechas se hace antes de llamar).
  const camposFiltro = useMemo(
    () => [
      { name: "origen_registro", label: "Origen", tipo: "select", opciones: origenes, opcionTodos: "TODOS" },
      { name: "fecha_desde", label: "Fecha desde", tipo: "fecha", max: (v) => v.fecha_hasta },
      { name: "fecha_hasta", label: "Fecha hasta", tipo: "fecha", min: (v) => v.fecha_desde },
      // { name: "tipo", label: "Tipo", tipo: "select", opciones: [{ label: "progeso", value: 1 }, { label: "evaluacion", value: 2 }] },

    ],
    [origenes]
  );

  useEffect(() => {
    cargarOrigenes();
    cargarListado(FILTROS_INICIALES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLimpiarFiltros = () => {
    setBusqueda("");
    cargarListado(FILTROS_INICIALES);
  };

  // Búsqueda local sobre el resultado del backend.
  const listadoFiltrado = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    if (!texto) return listado;
    return listado.filter((row) =>
      CAMPOS_BUSQUEDA.some((campo) => row[campo] != null && String(row[campo]).toLowerCase().includes(texto))
    );
  }, [listado, busqueda]);

  useEffect(() => setPagina(1), [busqueda, porPagina]);

  const renglonesPagina = useMemo(() => {
    const inicio = (pagina - 1) * porPagina;
    return listadoFiltrado.slice(inicio, inicio + porPagina);
  }, [listadoFiltrado, pagina, porPagina]);

  // Conteo por origen y totales: solo se suman importes con valor (null ≠ 0).
  const resumen = useMemo(() => {
    const porOrigen = {};
    const totales = Object.fromEntries(CAMPOS_IMPORTE.map((c) => [c, 0]));
    const conValor = Object.fromEntries(CAMPOS_IMPORTE.map((c) => [c, 0]));
    listadoFiltrado.forEach((row) => {
      porOrigen[row.origen_registro] = (porOrigen[row.origen_registro] || 0) + 1;
      CAMPOS_IMPORTE.forEach((campo) => {
        if (esNumero(row[campo])) {
          totales[campo] += Number(row[campo]);
          conValor[campo] += 1;
        }
      });
    });
    return { porOrigen, totales, conValor };
  }, [listadoFiltrado]);

  const origenesKpi = origenes.length ? origenes : Object.keys(ORIGEN_ESTILO);

  return (
    <PlantillaScreenRemastered
      modulo="Cobranza"
      titulo="Préstamos clientes"
      className="reporte-prestamos-clientes"
      onRegresar={() => history.push(RUTA_DASHBOARD)}
      filtro={({ cerrar }) => (
        <FiltroPanel
          tituloAlerta={TITULO_SWAL}
          campos={camposFiltro}
          valores={filtrosAplicados}
          validar={validarFiltros}
          onAplicar={(valores) => {
            cargarListado(valores);
            cerrar();
          }}
          // asd={{ region: true, sucursal: true, departamento: true}}
          onCerrar={cerrar}
        />
      )}
      filtrosActivos={contarFiltrosActivos(filtrosAplicados)}
      onExcel={generarExcel}
      excelDeshabilitado={listado.length === 0}
    >
      <LoaderScreen loader={loader} />
      <div className="rpc-contenido">
        <div className="rpc-kpis">
          <div className="rpc-kpi rpc-kpi--neutral">
            <span className="rpc-kpi-valor">{listadoFiltrado.length.toLocaleString("es-MX")}</span>
            <span className="rpc-kpi-etiqueta">Total registros</span>
          </div>
          {origenesKpi.map((origen) => (
            <div key={origen} className={`rpc-kpi rpc-kpi--${ORIGEN_ESTILO[origen] || "neutral"}`}>
              <span className="rpc-kpi-valor">{(resumen.porOrigen[origen] || 0).toLocaleString("es-MX")}</span>
              <span className="rpc-kpi-etiqueta">{origen.toLowerCase()}</span>
            </div>
          ))}
          <div className="rpc-kpi rpc-kpi--neutral">
            <span className="rpc-kpi-valor rpc-kpi-valor--importe">
              {resumen.conValor.saldo ? `$${FormatImporteNullable(resumen.totales.saldo)}` : SIN_VALOR}
            </span>
            <span className="rpc-kpi-etiqueta">Saldo total</span>
          </div>
        </div>

        <div className="rpc-barra">
          <div className="rpc-buscador">
            <SearchIcon />
            <input
              type="text"
              placeholder="Buscar documento, folio, cliente o RFC"
              value={busqueda}
              maxLength={100}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            {busqueda && (
              <button type="button" aria-label="Limpiar búsqueda" onClick={() => setBusqueda("")}>
                <ClearIcon />
              </button>
            )}
          </div>
          {contarFiltrosActivos(filtrosAplicados) > 0 && (
            <button type="button" className="rpc-quitar-filtros" onClick={onLimpiarFiltros}>
              Quitar filtros
            </button>
          )}
        </div>

        <div className="rpc-tabla scroll">
          <table cellPadding={0} cellSpacing={0}>
            <thead>
              <tr>
                {COLUMNAS.map((col) => (
                  <th key={col.campo} className={col.tipo === "importe" ? "rpc-num" : ""}>{col.etiqueta}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renglonesPagina.length === 0 ? (
                <tr>
                  <td className="rpc-vacio" colSpan={COLUMNAS.length}>
                    {loader.mostrar ? "Cargando información..." : "No se encontraron préstamos con los filtros seleccionados."}
                  </td>
                </tr>
              ) : (
                renglonesPagina.map((row, index) => (
                  <tr key={`${row.origen_registro}-${row.documento}-${row.folio_solicitud ?? ""}-${index}`}>
                    {COLUMNAS.map((col) => {
                      const texto = formatearCelda(row[col.campo], col.tipo);
                      if (col.campo === "origen_registro") {
                        return (
                          <td key={col.campo}>
                            <span className={`rpc-origen rpc-origen--${ORIGEN_ESTILO[row.origen_registro] || "neutral"}`}>
                              {texto}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={col.campo}
                          className={`${col.tipo === "importe" ? "rpc-num" : ""} ${texto === SIN_VALOR ? "rpc-sin-valor" : ""}`}
                          title={col.campo === "razon_social" ? texto : undefined}
                        >
                          {texto}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
            {listadoFiltrado.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={7}>Totales ({listadoFiltrado.length.toLocaleString("es-MX")} registros)</td>
                  {CAMPOS_IMPORTE.map((campo) => (
                    <td key={campo} className="rpc-num">
                      {resumen.conValor[campo] ? FormatImporteNullable(resumen.totales[campo]) : SIN_VALOR}
                    </td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <PaginadorListado
          pagina={pagina}
          totalRegistros={listadoFiltrado.length}
          porPagina={porPagina}
          onCambiarPagina={setPagina}
          onCambiarPorPagina={setPorPagina}
        />
      </div>
    </PlantillaScreenRemastered>
  );
};
